import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotepad } from '../contexts/NotepadContext';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import {
  clipKindIcon,
  clipKindLabel,
  clipTitle,
  authorColor,
  authorInitials,
  clipDeepLink,
  visibilityMeta,
  visibilityLabel,
} from '../domain/notepad';
import NotepadExportModal from './NotepadExportModal';
import ShareNotebookModal from './ShareNotebookModal';

/** Floating button (bottom-right) that opens the notepad and shows clip count. */
export function NotepadFab() {
  const notepad = useNotepad();
  if (!notepad || notepad.drawerOpen) return null;
  return (
    <button
      className="np-fab"
      title="Notepad — your case clips & notes"
      onClick={notepad.openDrawer}
    >
      🗒️
      {notepad.count > 0 && <span className="np-fab-count">{notepad.count}</span>}
    </button>
  );
}

// ── Saved searches (per-case, on-device, like inventory saved views) ──────
const searchesKey = (caseId) => `cs:notepad:${caseId}:searches`;
function loadSearches(caseId) {
  try { return JSON.parse(localStorage.getItem(searchesKey(caseId))) || []; }
  catch { return []; }
}
function persistSearches(caseId, arr) {
  try { localStorage.setItem(searchesKey(caseId), JSON.stringify(arr)); } catch { /* ignore */ }
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Tag chips on a clip; editable (add/remove) for the author. */
function ClipTags({ clip, isMine }) {
  const { updateClip, caseTags } = useNotepad();
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState('');
  const tags = clip.tags || [];
  // Autocomplete from the case-wide vocabulary, minus tags already on this clip
  // — nudges reuse of an existing tag instead of spawning a near-duplicate.
  const onClip = new Set(tags.map(t => t.toLowerCase()));
  const suggestions = (caseTags || [])
    .map(t => t.tag)
    .filter(t => !onClip.has(t.toLowerCase()));
  const listId = `np-taglist-${clip.id}`;

  const commit = () => {
    const t = val.trim();
    setVal(''); setAdding(false);
    if (!t || tags.some(x => x.toLowerCase() === t.toLowerCase())) return;
    updateClip(clip.id, { tags: [...tags, t] });
  };
  const remove = (t) => updateClip(clip.id, { tags: tags.filter(x => x !== t) });

  if (!isMine && tags.length === 0) return null;
  return (
    <div className="np-tags">
      {tags.map(t => (
        <span key={t} className="np-tag">
          {t}
          {isMine && <button className="np-tag-x" title="Remove tag" onClick={() => remove(t)}>×</button>}
        </span>
      ))}
      {isMine && (adding ? (
        <>
          <input
            autoFocus
            className="np-tag-input"
            list={listId}
            value={val}
            placeholder="tag…"
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setVal(''); setAdding(false); }
            }}
            onBlur={commit}
          />
          <datalist id={listId}>
            {suggestions.map(t => <option key={t} value={t} />)}
          </datalist>
        </>
      ) : (
        <button className="np-tag-add" onClick={() => setAdding(true)}>＋ tag</button>
      ))}
    </div>
  );
}

function ClipCard({ clip, isMine, autoFocus, onOpen, documentsById, defaultNotebookId, showNotebook }) {
  const { updateClip, deleteClip, moveClip, copyClip, notebooks } = useNotepad();
  const clipNotebook = showNotebook
    ? (notebooks || []).find(nb => nb.id === (clip.notebook_id || defaultNotebookId))
    : null;
  const [note, setNote] = useState(clip.note || '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const noteRef = useRef(null);
  const cardRef = useRef(null);
  const fileMenuRef = useRef(null);

  useEffect(() => { setNote(clip.note || ''); }, [clip.note]);
  // When this is the just-added clip, make the addition unmistakable even when
  // the drawer was already open: scroll it into view + flash it (the slide-open
  // animation is a no-op in that case). Reuses the .np-clip-flash keyframes.
  useEffect(() => {
    if (!autoFocus) return;
    cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    cardRef.current?.classList.add('np-clip-flash');
    noteRef.current?.focus();
  }, [autoFocus]);

  const color = authorColor(clip.author_id);
  const href = clipDeepLink(clip, useLocation().pathname);

  const saveNote = () => {
    const trimmed = note.trim();
    if (trimmed !== (clip.note || '')) updateClip(clip.id, { note: trimmed });
  };

  // Title: shows the attorney's saved name, or a derived default
  // ("Region - file.pdf, p.3"). Editing starts empty (default shows as
  // placeholder); clearing reverts to the default by saving "".
  const displayTitle = clipTitle(clip, documentsById);
  const startEditTitle = () => { setTitleDraft(clip.title || ''); setEditingTitle(true); };
  const commitTitle = () => {
    setEditingTitle(false);
    const t = titleDraft.trim();
    if (t !== (clip.title || '')) updateClip(clip.id, { title: t });
  };

  // Move/copy targets: every other notebook (move to current is a no-op,
  // copy to current is a same-place dupe). Only meaningful with >1 notebook.
  const currentNbId = clip.notebook_id || defaultNotebookId;
  const otherNotebooks = (notebooks || []).filter(nb => nb.id !== currentNbId);
  const closeFileMenu = () => { if (fileMenuRef.current) fileMenuRef.current.open = false; };

  return (
    <div ref={cardRef} className="np-clip" style={{ borderLeftColor: color }}>
      <div className="np-clip-head">
        <span className="np-avatar" style={{ background: color }} title={clip.author_name || clip.author_id}>
          {authorInitials(clip.author_name, clip.author_id)}
        </span>
        <span className="np-clip-kind">{clipKindIcon(clip.kind)} {clipKindLabel(clip.kind)}</span>
        {clipNotebook && <span className="np-clip-nb" title="Notebook">🗂 {clipNotebook.name}</span>}
        <span className="np-clip-time">{relativeTime(clip.created_at)}</span>
        <div className="np-clip-actions">
          {otherNotebooks.length > 0 && (
            <details className="np-clip-file" ref={fileMenuRef}>
              <summary className="np-icon-btn" title="Move or copy to another notebook">🗂</summary>
              <div className="np-clip-file-menu">
                {isMine && (
                  <>
                    <div className="np-menu-label">Move to</div>
                    {otherNotebooks.map(nb => (
                      <button key={`m-${nb.id}`} className="np-menu-item"
                        onClick={() => { moveClip(clip.id, nb.id); closeFileMenu(); }}>
                        {nb.name}
                      </button>
                    ))}
                  </>
                )}
                <div className="np-menu-label">Copy to</div>
                {otherNotebooks.map(nb => (
                  <button key={`c-${nb.id}`} className="np-menu-item"
                    onClick={() => { copyClip(clip.id, nb.id); closeFileMenu(); }}>
                    {nb.name}
                  </button>
                ))}
              </div>
            </details>
          )}
          {isMine && (
            <button className="np-icon-btn" title={clip.pinned ? 'Unpin' : 'Pin to top'}
              onClick={() => updateClip(clip.id, { pinned: !clip.pinned })}>
              {clip.pinned ? '📌' : '📍'}
            </button>
          )}
          {isMine && (
            <button className="np-icon-btn np-danger" title="Delete clip" onClick={() => deleteClip(clip.id)}>🗑</button>
          )}
        </div>
      </div>

      {editingTitle && isMine ? (
        <input
          className="np-clip-title-input"
          autoFocus
          value={titleDraft}
          placeholder={displayTitle}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
            if (e.key === 'Escape') { setEditingTitle(false); }
          }}
        />
      ) : (
        <div
          className={`np-clip-title${isMine ? ' np-clip-title-editable' : ''}`}
          onClick={isMine ? startEditTitle : undefined}
          title={isMine ? 'Rename' : undefined}
        >
          {displayTitle}{isMine && <span className="np-clip-title-edit" aria-hidden="true"> ✎</span>}
        </div>
      )}

      {clip.quote && <blockquote className="np-clip-quote">{clip.quote}</blockquote>}

      {isMine ? (
        <textarea
          ref={noteRef}
          className="np-clip-note-input"
          placeholder="Add a note…"
          value={note}
          rows={2}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
        />
      ) : (
        clip.note && <p className="np-clip-note">{clip.note}</p>
      )}

      <ClipTags clip={clip} isMine={isMine} />

      <div className="np-clip-foot">
        {href && <button className="np-link-btn" onClick={() => onOpen(href)}>↩ Open source</button>}
        {!isMine && <span className="np-author-name">— {clip.author_name || 'Team member'}</span>}
      </div>
    </div>
  );
}

/**
 * Notebook tab strip. Click a tab to navigate; double-click your own tab to
 * rename; × deletes a non-default tab you authored (its clips move to default).
 */
function NotebookTabs({ notebooks, activeId, counts, myUid, onSelect, onCreate, onRename, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');

  if (!notebooks.length) return null;

  const startRename = (nb) => { setEditingId(nb.id); setDraft(nb.name); };
  const commitRename = (nb) => {
    const name = draft.trim();
    setEditingId(null);
    if (name && name !== nb.name) onRename(nb.id, { name });
  };

  return (
    <div className="np-nb-tabs" role="tablist">
      {notebooks.map(nb => {
        const isActive = nb.id === activeId;
        const isMine = !!myUid && nb.author_id === myUid;
        const count = counts[nb.id] || 0;
        if (editingId === nb.id) {
          return (
            <input
              key={nb.id}
              autoFocus
              className="np-nb-rename-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename(nb);
                if (e.key === 'Escape') setEditingId(null);
              }}
              onBlur={() => commitRename(nb)}
            />
          );
        }
        return (
          <button
            key={nb.id}
            role="tab"
            aria-selected={isActive}
            className={`np-nb-tab ${isActive ? 'on' : ''}`}
            style={nb.color ? { borderBottomColor: nb.color } : undefined}
            title={isMine ? 'Click to open · double-click to rename' : nb.name}
            onClick={() => onSelect(nb.id)}
            onDoubleClick={() => isMine && startRename(nb)}
          >
            {!nb.is_default && nb.visibility && nb.visibility !== 'organization' && (
              <span className="np-nb-vis" title={visibilityLabel(nb)} aria-hidden="true">
                {visibilityMeta(nb.visibility).icon}
              </span>
            )}
            <span className="np-nb-name">{nb.name}</span>
            <span className="np-nb-count">{count}</span>
            {isMine && !nb.is_default && (
              <span
                className="np-nb-x"
                title="Delete notebook"
                onClick={(e) => { e.stopPropagation(); onDelete(nb); }}
              >×</span>
            )}
          </button>
        );
      })}
      <button className="np-nb-add" title="New notebook" onClick={onCreate}>＋</button>
    </div>
  );
}

export default function NotepadDrawer() {
  const notepad = useNotepad();
  const { user } = useAuth();
  const { documents } = useApp();
  const navigate = useNavigate();

  // Filename lookup for deriving clip default titles ("Region - file.pdf").
  const documentsById = useMemo(() => {
    const map = {};
    (documents || []).forEach(d => { map[d.id] = d; });
    return map;
  }, [documents]);
  const [draft, setDraft] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [shareNb, setShareNb] = useState(null);

  // Filter/search state (client-side over the loaded, decrypted clip set).
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState(() => new Set());
  const [tagFilter, setTagFilter] = useState(() => new Set());
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);

  const myUid = user?.uid;
  const caseId = notepad?.caseId;
  const clips = notepad?.clips || [];
  const filterMine = notepad?.filterMine || false;
  const notebooks = notepad?.notebooks || [];
  const activeNotebookId = notepad?.activeNotebookId || null;
  const caseTags = notepad?.caseTags || [];

  // Load saved searches when the case changes.
  useEffect(() => { setSavedSearches(caseId ? loadSearches(caseId) : []); }, [caseId]);

  // A clip's effective notebook: its own, or the case default for legacy clips
  // captured before notebooks existed (notebook_id null).
  const defaultNotebookId = useMemo(
    () => notebooks.find(n => n.is_default)?.id || null,
    [notebooks]
  );
  const clipNotebookId = (c) => c.notebook_id || defaultNotebookId;

  // Clip count per notebook (across the full case set) — drives tab badges.
  const notebookCounts = useMemo(() => {
    const m = {};
    clips.forEach(c => {
      const id = clipNotebookId(c);
      if (id) m[id] = (m[id] || 0) + 1;
    });
    return m;
  }, [clips, defaultNotebookId]);

  // The active notebook's clips — everything below is scoped to these.
  const notebookClips = useMemo(() => {
    if (!activeNotebookId) return clips;
    return clips.filter(c => clipNotebookId(c) === activeNotebookId);
  }, [clips, activeNotebookId, defaultNotebookId]);

  // Distinct facet values present in the active notebook's clip set.
  const allKinds = useMemo(
    () => Array.from(new Set(notebookClips.map(c => c.kind))),
    [notebookClips]
  );
  // Tag facets are CASE-WIDE (tags are cross-cutting): the vocabulary + counts
  // come from the server's tag endpoint, ordered most-used first.
  const tagCounts = useMemo(() => {
    const m = {};
    (caseTags || []).forEach(({ tag, count }) => { m[tag] = count; });
    return m;
  }, [caseTags]);
  const allTags = useMemo(() => (caseTags || []).map(t => t.tag), [caseTags]);

  // When a tag filter is active we search across ALL notebooks (case-wide),
  // since a tag like #alibi may span notebooks; otherwise stay in the open tab.
  const tagFiltering = tagFilter.size > 0;
  const filterBase = tagFiltering ? clips : notebookClips;

  const visible = useMemo(() => {
    let list = filterBase;
    if (filterMine && myUid) list = list.filter(c => c.author_id === myUid);
    if (pinnedOnly) list = list.filter(c => c.pinned);
    if (kindFilter.size) list = list.filter(c => kindFilter.has(c.kind));
    if (tagFilter.size) list = list.filter(c => (c.tags || []).some(t => tagFilter.has(t)));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(c =>
        (c.quote || '').toLowerCase().includes(q) ||
        (c.note || '').toLowerCase().includes(q) ||
        (c.author_name || '').toLowerCase().includes(q) ||
        (c.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [filterBase, filterMine, myUid, pinnedOnly, kindFilter, tagFilter, search]);

  const anyFilter = !!search.trim() || kindFilter.size > 0 || tagFilter.size > 0 || pinnedOnly;

  // All hooks above this line so ordering is stable across renders.
  if (!notepad) return null;
  const {
    loading, error, drawerOpen, closeDrawer,
    setFilterMine, addClip, justAddedId,
    setActiveNotebook, createNotebook, renameNotebook, deleteNotebook,
  } = notepad;

  const activeNotebook = notebooks.find(n => n.id === activeNotebookId) || null;

  const onCreateNotebook = () => {
    const name = window.prompt('Name this notebook:');
    if (name && name.trim()) createNotebook({ name: name.trim() });
  };
  const onDeleteNotebook = (nb) => {
    const n = notebookCounts[nb.id] || 0;
    const msg = n > 0
      ? `Delete "${nb.name}"? Its ${n} clip${n === 1 ? '' : 's'} will move to your default notebook.`
      : `Delete "${nb.name}"?`;
    if (window.confirm(msg)) deleteNotebook(nb.id);
  };

  const onOpen = (href) => { navigate(href); closeDrawer(); };

  const addQuickNote = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    await addClip({ kind: 'note', note: text });
  };

  const toggleSet = (setter) => (val) => setter(prev => {
    const next = new Set(prev);
    next.has(val) ? next.delete(val) : next.add(val);
    return next;
  });
  const toggleKind = toggleSet(setKindFilter);
  const toggleTag = toggleSet(setTagFilter);

  const clearFilters = () => {
    setSearch(''); setKindFilter(new Set()); setTagFilter(new Set()); setPinnedOnly(false);
  };

  const applySaved = (s) => {
    setSearch(s.search || '');
    setKindFilter(new Set(s.kinds || []));
    setTagFilter(new Set(s.tags || []));
    setPinnedOnly(!!s.pinnedOnly);
    setFilterMine(!!s.mine);
  };
  const saveCurrent = () => {
    const name = window.prompt('Name this search:');
    if (!name || !name.trim()) return;
    const entry = {
      name: name.trim(),
      search,
      kinds: Array.from(kindFilter),
      tags: Array.from(tagFilter),
      pinnedOnly,
      mine: filterMine,
    };
    const next = [...savedSearches.filter(s => s.name !== entry.name), entry];
    setSavedSearches(next);
    persistSearches(caseId, next);
  };
  const deleteSaved = (name) => {
    const next = savedSearches.filter(s => s.name !== name);
    setSavedSearches(next);
    persistSearches(caseId, next);
  };

  return (
    <>
    {/* `inert` (React 18 string-form) removes the closed drawer's off-screen
        textareas from tab order — aria-hidden alone leaves them focusable. */}
    <div
      className={`np-drawer ${drawerOpen ? 'np-open' : ''}`}
      aria-hidden={!drawerOpen}
      inert={drawerOpen ? undefined : ''}
    >
      <div className="np-header">
        <div className="np-title">
          🗒️ Notepad
          <span className="np-count">{anyFilter ? `${visible.length}/${filterBase.length}` : notebookClips.length}</span>
          {tagFiltering && <span className="np-scope-hint" title="Tag filters search every notebook">· all notebooks</span>}
        </div>
        <div className="np-header-actions">
          <div className="np-filter-toggle">
            <button className={!filterMine ? 'np-seg np-seg-on' : 'np-seg'} onClick={() => setFilterMine(false)}>All</button>
            <button className={filterMine ? 'np-seg np-seg-on' : 'np-seg'} onClick={() => setFilterMine(true)}>Mine</button>
          </div>
          <button className="np-icon-btn" title="Export a memo from these clips"
            onClick={() => setExportOpen(true)} disabled={visible.length === 0}>📤</button>
          <button className="np-icon-btn" title="Close" onClick={closeDrawer}>✕</button>
        </div>
      </div>

      {/* Notebook tabs — name, navigate, and export each independently. */}
      <NotebookTabs
        notebooks={notebooks}
        activeId={activeNotebookId}
        counts={notebookCounts}
        myUid={myUid}
        onSelect={setActiveNotebook}
        onCreate={onCreateNotebook}
        onRename={renameNotebook}
        onDelete={onDeleteNotebook}
      />

      {/* Active notebook sharing scope — owner can manage, others see the scope. */}
      {activeNotebook && !activeNotebook.is_default && (
        <div className="np-nb-share-bar">
          <span className="np-nb-share-scope" title={visibilityMeta(activeNotebook.visibility).desc}>
            {visibilityMeta(activeNotebook.visibility).icon} {visibilityLabel(activeNotebook)}
          </span>
          {!!myUid && activeNotebook.author_id === myUid ? (
            <button className="np-link-btn" onClick={() => setShareNb(activeNotebook)}>Share…</button>
          ) : (
            <span className="np-nb-share-owner">· {activeNotebook.author_name || 'Team member'}’s notebook</span>
          )}
        </div>
      )}

      {/* Search + filters */}
      <div className="np-search">
        <input
          className="np-search-input"
          placeholder="Search notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {anyFilter && <button className="np-clear" title="Clear filters" onClick={clearFilters}>Clear</button>}
      </div>

      {(allKinds.length > 1 || allTags.length > 0) && (
        <div className="np-filters">
          {allKinds.map(k => (
            <button key={k} className={`np-fchip ${kindFilter.has(k) ? 'on' : ''}`} onClick={() => toggleKind(k)}>
              {clipKindIcon(k)} {clipKindLabel(k)}
            </button>
          ))}
          <button className={`np-fchip ${pinnedOnly ? 'on' : ''}`} onClick={() => setPinnedOnly(p => !p)}>📌 Pinned</button>
          {allTags.map(t => (
            <button key={t} className={`np-fchip np-fchip-tag ${tagFilter.has(t) ? 'on' : ''}`} onClick={() => toggleTag(t)}>
              #{t}{tagCounts[t] ? <span className="np-fchip-n">{tagCounts[t]}</span> : null}
            </button>
          ))}
        </div>
      )}

      {(savedSearches.length > 0 || anyFilter) && (
        <div className="np-saved">
          {savedSearches.map(s => (
            <span key={s.name} className="np-saved-chip">
              <button className="np-saved-apply" onClick={() => applySaved(s)}>{s.name}</button>
              <button className="np-saved-x" title="Delete saved search" onClick={() => deleteSaved(s.name)}>×</button>
            </span>
          ))}
          {anyFilter && <button className="np-saved-save" onClick={saveCurrent}>＋ Save search</button>}
        </div>
      )}

      <div className="np-quickadd">
        <input
          className="np-quickadd-input"
          placeholder="Jot a quick note…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addQuickNote(); }}
        />
        <button className="np-add-btn" onClick={addQuickNote} disabled={!draft.trim()}>Add</button>
      </div>

      {error && <div className="np-error">{error}</div>}

      <div className="np-body">
        {loading && clips.length === 0 && <div className="np-empty">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="np-empty">
            {anyFilter ? 'No clips match your filters.' : (filterMine ? 'No clips of yours yet.' : 'No clips yet.')}
            {!anyFilter && (
              <p className="np-empty-hint">Highlight text in a document, save an Arc answer, or jot a note above.</p>
            )}
          </div>
        )}
        {visible.map(clip => (
          <ClipCard
            key={clip.id}
            clip={clip}
            isMine={!!myUid && clip.author_id === myUid}
            autoFocus={clip.id === justAddedId}
            onOpen={onOpen}
            documentsById={documentsById}
            defaultNotebookId={defaultNotebookId}
            showNotebook={tagFiltering}
          />
        ))}
      </div>
    </div>
    {exportOpen && (
      <NotepadExportModal
        clips={visible}
        notebookName={activeNotebook?.name}
        onClose={() => setExportOpen(false)}
      />
    )}
    {shareNb && (
      <ShareNotebookModal notebook={shareNb} onClose={() => setShareNb(null)} />
    )}
    </>
  );
}
